import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Courses";

function AllCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ── Filter & Pagination state ─────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Fetch all courses ──────────────────────────────────────────────────────
  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/GetAllCourses?isActive=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.data ?? []);
      setCourses(list);
    } catch (err) {
      console.error("❌ Error fetching courses:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // ── Delete with SweetAlert2 confirmation ──────────────────────────────────
  const handleDelete = async (courseId, courseTitle) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This course will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!result.isConfirmed) return;

    setDeletingId(courseId);
    try {
      const res = await fetch(
        `${BASE_URL}/DeleteCourse?courseId=${encodeURIComponent(courseId)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `${localStorage.getItem("SLNAdminToken") ?? ""}`,
          },
        },
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      setCourses((prev) =>
        prev.filter((c) => (c.courseId ?? c.id) !== courseId),
      );

      await Swal.fire({
        title: "Deleted!",
        text: "Course has been removed successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        customClass: { popup: "rounded-4" },
      });
    } catch (err) {
      console.error("❌ Delete failed:", err);
      await Swal.fire({
        title: "Delete Failed",
        text: err.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#e53935",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // ── Filter & Pagination derived data ──────────────────────────────────────
  const filteredCourses = courses.filter((c) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      String(c.title ?? "")
        .toLowerCase()
        .includes(q) ||
      String(c.category ?? "")
        .toLowerCase()
        .includes(q) ||
      String(c.courseId ?? "")
        .toLowerCase()
        .includes(q) ||
      String(c.instructors?.[0]?.name ?? "")
        .toLowerCase()
        .includes(q) ||
      String(c.originalPrice ?? "")
        .toLowerCase()
        .includes(q) ||
      String(c.offeredPrice ?? "")
        .toLowerCase()
        .includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const paginatedCourses = filteredCourses.slice(startIdx, startIdx + pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-lg-12 col-md-12 col-12">
              <div className="pb-3 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div className="d-flex flex-column">
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    All Courses{" "}
                    {!loading && !error && (
                      <span className="text-secondary">({courses.length})</span>
                    )}
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Manage Courses / Add Courses / Update Courses / Trash
                    Courses
                  </p>
                </div>
                <div>
                  <Link
                    to="/manage/add-course"
                    className="btn btn-primary fw-normal px-5 py-1 rounded-3"
                  >
                    Add Courses
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* ── Loading ── */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
              <p className="mt-2 text-secondary">Fetching courses…</p>
            </div>
          )}

          {/* ── Error ── */}
          {!loading && error && (
            <div className="alert alert-danger rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-triangle-fill fs-5"></i>
              <div>
                <strong>Failed to load courses.</strong> {error}
                <button
                  className="btn btn-sm btn-outline-danger ms-3 rounded-3"
                  onClick={fetchCourses}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* ── Search & Page Size ── */}
          {!loading && !error && (
            <div className="row g-2 align-items-center mb-3">
              <div className="col-md-6 col-12">
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by title, category, instructor, course ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                      title="Clear"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3 col-6">
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
              <div className="col-md-3 col-6 text-md-end text-secondary small">
                Showing {filteredCourses.length === 0 ? 0 : startIdx + 1}–
                {Math.min(startIdx + pageSize, filteredCourses.length)} of{" "}
                {filteredCourses.length}
              </div>
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && (
            <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4 mb-6">
              <table className="table mb-0 text-nowrap table-centered">
                <thead className="table-light">
                  <tr>
                    <th className="w-10 py-2">No.</th>
                    <th className="lh-1 w-60 py-2">
                      Details
                      <i className="bi bi-info-circle ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Displaying Information as per Record
                      </span>
                    </th>
                    <th className="lh-1 w-30 py-2">
                      Action
                      <i className="bi bi-person-gear ms-2 text-dark"></i>
                      <br />
                      <span className="fs-8 fw-normal text-secondary">
                        Role-Specific Action Panel
                      </span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredCourses.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="text-center py-5 text-secondary"
                      >
                        <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                        {courses.length === 0
                          ? "No courses found."
                          : "No courses match your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedCourses.map((course, idx) => {
                      const id = course.courseId ?? `unknown-${idx}`;
                      const title = course.title ?? "Untitled";
                      const category = course.category ?? "—";
                      const originalPrice = course.originalPrice;
                      const offeredPrice = course.offeredPrice;
                      const instructorName =
                        course.instructors?.[0]?.name ?? "—";
                      const instructorRating =
                        course.instructors?.[0]?.rating ?? null;
                      const lessonCount = course.lessons?.length ?? 0;
                      const banner = course.banner ?? null;
                      const dateRaw = course.createdAt ?? null;
                      const updatedAt = course.updatedAt ?? null;
                      const status = course.status ?? null;
                      const featured = course.featured ?? null;

                      const isDeleting = deletingId === id;

                      return (
                        <tr key={id} style={{ opacity: isDeleting ? 0.5 : 1 }}>
                          <td>{startIdx + idx + 1}.</td>

                          <td className="lh-sm py-4">
                            <span className="text-dark">
                              <strong>Title:</strong>
                            </span>{" "}
                            {title}
                            <br />
                            <span className="text-dark">
                              <strong>Course ID:</strong>
                            </span>{" "}
                            <code className="text-secondary">{id}</code>
                            <br />
                            <span className="text-dark">
                              <strong>Category:</strong>
                            </span>{" "}
                            {category}
                            <br />
                            <span className="text-dark">
                              <strong>Instructor:</strong>
                            </span>{" "}
                            {instructorName}
                            {instructorRating && (
                              <span className="ms-1 text-warning">
                                ★ {instructorRating}
                              </span>
                            )}
                            <br />
                            <span className="text-dark">
                              <strong>Price:</strong>
                            </span>{" "}
                            {offeredPrice != null ? (
                              <>
                                <span className="fw-semibold text-success">
                                  ₹{offeredPrice}
                                </span>
                                {originalPrice != null && (
                                  <span className="text-decoration-line-through text-secondary ms-2 small">
                                    ₹{originalPrice}
                                  </span>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                            <br />
                            <span className="text-dark">
                              <strong>Lessons:</strong>
                            </span>{" "}
                            {lessonCount}
                            <br />
                            <span className="text-dark">
                              <strong>Created:</strong>
                            </span>{" "}
                            {formatDate(dateRaw)}
                            <br />
                            <span className="text-dark">
                              <strong>Status:</strong>
                            </span>{" "}
                            <span className="text-capitalize">{status}</span>
                            <br />
                            <span className="text-dark">
                              <strong>Featured:</strong>
                            </span>{" "}
                            {featured ? "Yes" : "Not Featured"}
                            <br />
                            <br />
                            <span className="text-dark">
                              <strong>Updated On:</strong>
                            </span>{" "}
                            {formatDate(updatedAt)}
                          </td>

                          <td>
                            <div className="d-flex gap-1 align-items-start">
                              {/* Edit button (wire up as needed) */}
                              <button
                                className="btn p-0 border-0 bg-transparent"
                                title="Edit"
                                onClick={() =>
                                  navigate("/manage/add-course", {
                                    state: { courseId: id },
                                  })
                                }
                              >
                                <div className="icon-shape icon-md bg-light-warning rounded-circle">
                                  <i className="bi bi-pencil-square fs-4 text-dark"></i>
                                </div>
                              </button>

                              {/* Delete button */}
                              <button
                                className="btn p-0 border-0 bg-transparent"
                                title="Delete"
                                disabled={isDeleting}
                                onClick={() => handleDelete(id, title)}
                              >
                                <div className="icon-shape icon-md bg-light-danger rounded-circle">
                                  {isDeleting ? (
                                    <span
                                      className="spinner-border spinner-border-sm text-danger"
                                      role="status"
                                    />
                                  ) : (
                                    <i className="bi bi-trash fs-4 text-dark"></i>
                                  )}
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!loading &&
            !error &&
            filteredCourses.length > 0 &&
            totalPages > 1 && (
              <nav aria-label="Courses pagination" className="mt-3">
                <ul className="pagination justify-content-center mb-0 flex-wrap">
                  <li
                    className={`page-item ${safePage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(1)}
                      disabled={safePage === 1}
                    >
                      <i className="bi bi-chevron-double-left"></i>
                    </button>
                  </li>
                  <li
                    className={`page-item ${safePage === 1 ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {getPageNumbers().map((num) => (
                    <li
                      key={num}
                      className={`page-item ${num === safePage ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(num)}
                      >
                        {num}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={safePage === totalPages}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                  <li
                    className={`page-item ${safePage === totalPages ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={safePage === totalPages}
                    >
                      <i className="bi bi-chevron-double-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
        </div>
      </section>
    </>
  );
}

export default AllCourses;
